import React, { useState } from "react";
import { ArrowPathIcon, ScaleIcon } from "@heroicons/react/24/outline";
import { fetchWithSupabaseAuth } from "../../lib/supabaseClient.js";
import { invalidatePageDataCacheEntries } from "../../hooks/usePageData.js";
import { dispatchPlanningStateChange } from "../../utils/planningCrossViewSync.js";
import { tryBeginPlanningUpdateActivity } from "../../utils/planningMutationActivity.js";

// Catégorie « Produits en expansion (EXP) » de Mise à jour : camions dont le plan dépasse la limite
// EXP de la date. Recalculer = chargements du jour; Conserver tel quel = chargement de la veille.
const ACTION_CLASS_NAME = "inline-flex h-6 items-center justify-center gap-1 rounded-[4px] border px-2 py-0 text-[0.65rem] font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100 disabled:shadow-none";
const tons = (value) => `${String(Math.round(Number(value) * 10) / 10).replace(".", ",")} t`;

export function describeCompartmentFillViolation(item) {
  const compartment = Number.isFinite(Number(item?.position)) && item?.position !== null ? `C${Number(item.position)}` : item?.compartment_id || "Benne";
  return `${item?.order_id}${item?.is_preload ? " · veille" : ""} · ${compartment} : ${tons(item?.quantity_tons)} > ${tons(item?.max_tons)} (${item?.fill_percent} %)`;
}

export default function CompartmentFillChanges({
  Header,
  actionsDisabled = false,
  bodyClassName = "",
  cardClassName = "",
  details = [],
  dispatchDate = "",
  dispatchPeriod = "day",
  isBusy = false,
  onOpenTruck,
  onRefresh,
  runtimeRevision = "",
  sectionClassName = "",
  truckLabelById = {},
}) {
  const headingId = React.useId();
  const [pendingKey, setPendingKey] = useState("");
  const [errorByTruckId, setErrorByTruckId] = useState({});
  if (!details.length) {
    return null;
  }

  const run = async (detail, action) => {
    const truckId = detail.truck_id;
    const finishActivity = tryBeginPlanningUpdateActivity();
    if (!finishActivity) {
      setErrorByTruckId((current) => ({ ...current, [truckId]: "Une mise à jour du planning est déjà en cours." }));
      return;
    }
    setPendingKey(`${truckId}:${action}`);
    setErrorByTruckId((current) => ({ ...current, [truckId]: "" }));
    try {
      const params = new URLSearchParams({ dispatch_date: dispatchDate, dispatch_period: dispatchPeriod });
      const response = await fetchWithSupabaseAuth(`/api/planning/source-changes/compartment-fill/${action}?${params}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          truck_id: truckId,
          expected_runtime_revision: runtimeRevision,
          ...(action === "keep" ? { order_ids: detail.exp_preload_order_ids } : {}),
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || payload?.detail?.error || (typeof payload?.detail === "string" ? payload.detail : "Action impossible. Rien n'a été modifié."));
      }
    } catch (failure) {
      setErrorByTruckId((current) => ({ ...current, [truckId]: failure instanceof Error ? failure.message : "Action impossible. Rien n'a été modifié." }));
    } finally {
      invalidatePageDataCacheEntries(["/api/results/latest"]);
      dispatchPlanningStateChange("dispatch", {
        dispatchDate, dispatchPeriod: "", action: `compartment_fill_${action}`, revalidateCurrentView: true,
        // Cet onglet revérifie Mise à jour juste après (onRefresh); les autres onglets actualisent.
        skipLocalSourceChangesRefresh: true,
      });
      setPendingKey("");
      finishActivity();
      onRefresh?.();
    }
  };

  return (
    <section aria-labelledby={headingId} className={sectionClassName} data-compartment-fill-section>
      <Header count={details.length} headingId={headingId} icon={ScaleIcon} label="Produits en expansion (EXP)" />
      <div className={bodyClassName}>
        {details.map((detail) => {
          const truckId = detail.truck_id;
          const truckLabel = detail.truck_label || truckLabelById[truckId] || truckId;
          const canRecalculate = detail.order_ids?.length > 0;
          const canKeep = detail.preload_order_ids?.length > 0;
          const disabled = actionsDisabled || isBusy || Boolean(pendingKey);
          return (
            <article key={truckId} className={cardClassName} data-compartment-fill-truck={truckId}>
              <button
                type="button"
                role="menuitem"
                className="min-w-0 rounded-[4px] text-left text-[0.72rem] font-bold leading-tight text-slate-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/35"
                aria-label={`Ouvrir ${truckLabel}`}
                disabled={isBusy || typeof onOpenTruck !== "function"}
                onClick={() => onOpenTruck?.(detail)}
              >{truckLabel}</button>
              <div className="grid min-w-0 gap-0.5 text-[0.64rem] leading-4 text-slate-700">
                {(detail.violations || []).map((item) => (
                  <p key={`${item.order_id}|${item.compartment_id}`} className="m-0 min-w-0">{describeCompartmentFillViolation(item)}</p>
                ))}
              </div>
              {errorByTruckId[truckId] ? (
                <p role="alert" className="m-0 text-[0.64rem] font-semibold leading-4 text-rose-700">{errorByTruckId[truckId]}</p>
              ) : null}
              <div className="flex flex-wrap gap-1">
                {canRecalculate ? (
                  <button
                    type="button"
                    role="menuitem"
                    aria-busy={pendingKey === `${truckId}:recalculate`}
                    className={`${ACTION_CLASS_NAME} border-accent bg-accent text-white hover:border-accent-strong hover:bg-accent-strong`}
                    data-compartment-fill-recalculate={truckId}
                    disabled={disabled}
                    onClick={() => void run(detail, "recalculate")}
                  >
                    <ArrowPathIcon aria-hidden="true" className={`h-3.5 w-3.5 ${pendingKey === `${truckId}:recalculate` ? "motion-safe:animate-spin" : ""}`} />
                    {pendingKey === `${truckId}:recalculate` ? "Recalcul…" : "Recalculer"}
                  </button>
                ) : null}
                {canKeep ? (
                  <button
                    type="button"
                    role="menuitem"
                    aria-busy={pendingKey === `${truckId}:keep`}
                    className={`${ACTION_CLASS_NAME} border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900`}
                    data-compartment-fill-keep={truckId}
                    disabled={disabled}
                    onClick={() => void run(detail, "keep")}
                    title="Le chargement de la veille est déjà dans le camion : ses commandes EXP sont exemptées de la limite pour cette date."
                  >
                    {pendingKey === `${truckId}:keep` ? "Enregistrement…" : "Conserver tel quel"}
                  </button>
                ) : null}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
